import { AssumptionService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AssessmentConfiguration, NewAssumptionConfig } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, FlashContextInterface, FlashType, withAuthContext, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Checkbox, Container, Form, FormField, Input, InputProps, SpaceBetween } from '@amzn/awsui-components-react';
import { NonCancelableCustomEvent } from '@amzn/awsui-components-react/polaris/internal/events';
import { useState } from 'react';

import { constants } from '../../common/Localization';
import { ASSUMPTION_PREFIX, ASSUMPTION_TYPE } from './tco-assumptions-modal-helper';
import TcoAssumptionList from './TcoAssumptionList';

const assumptionService = new AssumptionService();
type Props = AuthContextInterface & FlashContextInterface & {
    configuration: AssessmentConfiguration;
    cancel: () => void;
    onSaveAssumption: (assumptionName: string) => void;
    saveAsNew: boolean;
    assumptionName?: string;
};

function TcoAssumptionSaveOrOverride(props: Props): JSX.Element {
    const [ name, setName ] = useState('');
    const [ errorText, setErrorText ] = useState('');
    const [ description, setDescription ] = useState('');
    const [ savedAsNewChecked, setSavedAsNewChecked ] = useState(props.saveAsNew);
    const [ saveLoading, setSaveLoading ] = useState(false);

    const onNameChange = (event: NonCancelableCustomEvent<InputProps>) => {
        setName(event.detail.value);
        setErrorText(isInvalidName(event.detail.value) ? constants.INVALID_ASSUMPTION_NAME : '');
    };

    const isInvalidName = (name: string): boolean => {
        return name.length < 5 || name.length > 100;
    };

    const onConfirmSave = async () => {
        setSaveLoading(true);
        const userInfo = await props.auth.getUserInfo();
        if (!userInfo || !userInfo.userId) {
            props.flash.alert(FlashType.error, constants.INVALID_USER_INFO, '');
            return;
        }

        const config: NewAssumptionConfig = {
            config: {
                description,
                name,
                prefix: ASSUMPTION_PREFIX,
                resourceId: userInfo.userId,
                type: ASSUMPTION_TYPE,
            },
            data: props.configuration,
        };
        await assumptionService.saveAsNew(config);
        props.onSaveAssumption(name);
    };

    const saveContainer = savedAsNewChecked ? 
        (
            <form onSubmit={e => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction='horizontal' size='xs'>
                            <Button
                                disabled={saveLoading}
                                formAction='none'
                                variant='link'
                                onClick={props.cancel}
                            >{constants.CANCEL_BUTTON_TEXT}
                            </Button>
                            <Button
                                loading={saveLoading}
                                variant='primary'
                                onClick={onConfirmSave}
                                disabled={errorText.length>0 || name.length===0}
                                id='btn-apply-tco-assumption'
                            >{constants.SAVE_AND_APPLY}</Button>
                        </SpaceBetween>
                    }
                >
                    <Container>
                        <SpaceBetween direction='vertical' size='l'>
                            <FormField label={constants.NAME + '*'} errorText={errorText}>
                                <Input value={name} onChange={onNameChange}/>
                            </FormField>
                            <FormField label={constants.DESCRIPTION}>
                                <Input value={description} onChange={({detail}) => setDescription(detail.value)}/>
                            </FormField>
                        </SpaceBetween>
                    </Container>
                </Form>
            </form>
        ) : 
        (
            <TcoAssumptionList
                configuration={props.configuration}
                cancel={props.cancel}
                onSaveAssumption={props.onSaveAssumption}
                isSaveAsExisting={true}
                assumptionName={props?.assumptionName}
                title={savedAsNewChecked ? constants.SAVE_NEW_ASSUMPTION_TITLE : constants.OVERRIDE_ASSUMPTION_TITLE}
            />
        );

    const title = savedAsNewChecked ? (<Box variant='h2'>{constants.SAVE_NEW_ASSUMPTION_TITLE}</Box>) : null;

    return (
        <SpaceBetween direction='vertical' size='m'>
            <Checkbox
                onChange={({ detail }) =>
                    setSavedAsNewChecked(detail.checked)
                }
                checked={savedAsNewChecked}
            >
                {constants.SAVE_AS_NEW}
            </Checkbox>
            {title}
            {saveContainer}
        </SpaceBetween>
    );
}

export default withAuthContext(withFlashContext(TcoAssumptionSaveOrOverride));
